import {
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useLazyGetBusinessesByRegionQuery } from "lib/features/business/businessService";
import { Business } from "lib/features/business/types";

import DatePickerModal from "app/components/Business/DatePickerModal";
import dayjs, { Dayjs } from "dayjs";
import { useLazyGetItemsByRegionAndDateQuery } from "lib/features/item/itemService";
import { useEffect, useState } from "react";

import { selectUser } from "lib/features/user/userSlice";

import { useAppDispatch, useAppSelector } from "lib/hooks";

import {
  selectBrowseType,
  setBrowseType,
} from "lib/features/common/commonSlice";

import SearchIcon from "@mui/icons-material/Search";
import AllCategories from "app/components/AllCategories";
import AvailabilityCard from "app/components/Business/AvailabilityCard";
import FareharborAvailabilityCard from "app/components/Business/FareharborAvailabilityCard";
import BusinessCard from "app/components/BusinessCard";
import TabButton from "app/components/TabButton";
import { Availability } from "lib/features/availability/types";
import { Category } from "lib/features/category/categoryService";
import { Item } from "lib/features/item/types";
import moment from "moment";
import authServices from "services/auth-service";
import { homeScreenStyles } from "styles/Home/homeScreen.styles";

export const Home = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const browseType = useAppSelector(selectBrowseType);
  const urlParams = new URLSearchParams(window.location.search);
  const dateParam = urlParams.get("date");

  /////* state varaibles */////
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<{
    id: number;
    name: string;
  }>({ id: 2, name: "All" });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUserLoading, setIsUserLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [userData, setUserData] = useState<any>({});
  const [date, setDate] = useState<Dayjs>(
    dateParam ? dayjs(dateParam) : dayjs()
  );
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [businessList, setBusinessList] = useState<Business[]>([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState<Business[]>([]);

  /////* API Calls */////

  const [
    getBusinessesByRegion,
    {
      data: businessesData,
      error: businessesError,
      isLoading: businessesLoading,
    },
  ] = useLazyGetBusinessesByRegionQuery();

  const [
    getItemsByRegionAndDate,
    { data: items, error: itemsError, isLoading: itemsLoading },
  ] = useLazyGetItemsByRegionAndDateQuery();

  /////* helper function */////

  const handleDateChange = (newDate: Dayjs | null) => {
    if (!newDate) return;

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("date", newDate.format("YYYY-MM-DD"));

    window.history.pushState(
      {},
      "",
      `${window.location.pathname}?${searchParams.toString()}`
    );

    setDate(newDate);
    setIsCalendarVisible(false);
  };

  const handleActiveCategory = (id: number, name: string, index: number) => {
    if (index != activeCategoryIndex) {
      setActiveCategoryIndex(index);
      setSelectedCategory({ id, name });
    }
  };

  const extractBookableDates = (availabilities: Availability[]): string[] => {
    return availabilities
      ?.map((availability) =>
        moment.utc(availability.start_time).format("YYYY-MM-DD")
      )
      ?.filter((date, index, self) => self.indexOf(date) === index);
  };

  const applyCategoryFilter = (businesses: Business[]) => {
    let filteredBusinesses;
    if (selectedCategory.name === "All") {
      filteredBusinesses = businesses;
    } else if (selectedCategory.name === "Favorites") {
      filteredBusinesses = businesses.filter((business) => business.isFavorite);
    } else {
      filteredBusinesses = businesses.filter((business) =>
        business.categories.some(
          (category) => category.category === selectedCategory.name
        )
      );
    }
    filteredBusinesses = filteredBusinesses.filter((b) => b.record_status);
    setFilteredBusinesses(filteredBusinesses);
  };

  const filterBySearch = (itemsToFilter: Item[]) => {
    // First, remove private availabilities from each item without mutating the originals
    const availabilityFilteredItems = itemsToFilter
      .map((item) => {
        const publicAvailabilities = (item.availabilities || []).filter(
          (availability: any) => availability?.private !== true
        );

        const publicFareharborAvailabilities =
          (item.fareharbor_availabilities || []).filter(
            (availability: any) => availability?.private !== true
          );

        return {
          ...item,
          availabilities: publicAvailabilities,
          fareharbor_availabilities: publicFareharborAvailabilities,
        };
      })
      // Drop items that no longer have any public availabilities
      .filter((item) => {
        const hasStandardAvailabilities =
          (item.availabilities && item.availabilities.length > 0) || false;
        const hasFareharborAvailabilities =
          (item.fareharbor_availabilities &&
            item.fareharbor_availabilities.length > 0) || false;

        return hasStandardAvailabilities || hasFareharborAvailabilities;
      });

    if (!searchQuery.trim()) return availabilityFilteredItems;

    return availabilityFilteredItems.filter((item) => {
      const itemName = (
        item?.itemDetails?.[0]?.item_name?.toLowerCase() || ""
      ).includes(searchQuery.toLowerCase());
      const headline = (
        item?.itemDetails?.[0]?.headline?.toLowerCase() || ""
      ).includes(searchQuery.toLowerCase());
      const fareharborName = (
        item?.farehabor_item_details?.name?.toLowerCase() || ""
      ).includes(searchQuery.toLowerCase());
      const fareharborHeadline = (
        item?.farehabor_item_details?.headline?.toLowerCase() || ""
      ).includes(searchQuery.toLowerCase());

      return itemName || headline || fareharborName || fareharborHeadline;
    });
  };

  const filterBusinessesBySearch = (businessesToFilter: Business[]) => {
    if (!searchQuery.trim()) return businessesToFilter;

    return businessesToFilter.filter(
      (business) =>
        (business?.business?.toLowerCase() || "").includes(
          searchQuery.toLowerCase()
        ) ||
        (business?.business_headline?.toLowerCase() || "").includes(
          searchQuery.toLowerCase()
        )
    );
  };

  const handleToggleFavorite = (businessId: number) => {
    const updatedBusinesses = businessList.map((business) => {
      const updatedBusiness =
        business.id === businessId
          ? { ...business, isFavorite: !business.isFavorite }
          : business;

      return updatedBusiness;
    });

    setBusinessList(updatedBusinesses);
    applyCategoryFilter(updatedBusinesses);
  };

  /////* render functions */////

  const renderToggleButtons = () => (
    <Box sx={homeScreenStyles.toggleButtonsContainer(theme)}>
      <TabButton
        label="Bookable Items"
        onClick={() => {
          dispatch(setBrowseType("bookableExperience"));
        }}
        active={browseType === "bookableExperience"}
      />
      <TabButton
        label="Businesses"
        onClick={() => {
          dispatch(setBrowseType("business"));
        }}
        active={browseType === "business"}
      />
    </Box>
  );

  const renderSearchBar = () => (
    <TextField
      placeholder="Search"
      variant="outlined"
      size="small"
      fullWidth
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      sx={{
        mb: 2,
        borderRadius: "20px",
        borderColor: "#8D8D8D",
        ".MuiInputBase-root": { borderRadius: "20px" },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
      }}
    />
  );

  // Calculate visible items and businesses
  const categoryFilteredItems =
    items?.filter((i: Item) => {
      if (
        selectedCategory.name === "All" ||
        selectedCategory.name === "Favorites"
      ) {
        return true;
      } else {
        return i.categories.some(
          (c: Category) => c.category === selectedCategory.name
        );
      }
    }) || [];

  const visibleItems = filterBySearch(categoryFilteredItems);
  const visibleBusinesses = filterBusinessesBySearch(filteredBusinesses);

  const shouldFetchItems =
    userData?.regions?.length > 0 &&
    userData.regions[0]?.region?.id &&
    user.id &&
    selectedCategory.name;

  const renderBookableExperienceCards = () => {
    return (
      <>
        {isLoading || isUserLoading || (shouldFetchItems && !items) ? (
          <CircularProgress />
        ) : visibleItems?.length === 0 || !visibleItems ? (
          <Typography>No Availability found.</Typography>
        ) : (
          <Box
            sx={[
              homeScreenStyles.bookableContainer,
              {
                justifyContent: "flex-start",
                gap: "10px",
              },
            ]}
          >
            {visibleItems?.map((item: any, index: number) => {
              if (
                item?.item_type === 1 ||
                item?.item_type === 3 ||
                (item?.item_type === 4 &&
                  item.fareharbor_availabilities !== undefined &&
                  item.fareharbor_availabilities !== null &&
                  item.fareharbor_availabilities?.length > 0)
              ) {
                return (
                  <Box
                    key={index}
                    sx={homeScreenStyles.subCardsContainer(theme)}
                  >
                    {item?.item_type === 1 && <AvailabilityCard item={item} />}
                    {item?.item_type === 3 && <AvailabilityCard item={item} />}
                    {item?.item_type === 4 &&
                      item.fareharbor_availabilities?.length > 0 && (
                        <FareharborAvailabilityCard item={item} />
                      )}
                  </Box>
                );
              }
              return null;
            })}
          </Box>
        )}
      </>
    );
  };

  const renderBusinessCards = () => {
    return (
      <>
        {businessesLoading || isUserLoading ? (
          <CircularProgress />
        ) : visibleBusinesses?.length === 0 ? (
          <Typography>No business records found.</Typography>
        ) : (
          <Grid container spacing={2}>
            {visibleBusinesses?.map((business: Business, i: number) => (
              <Grid item key={i} xs={12} sm={6} md={4} lg={3}>
                <BusinessCard
                  business={business}
                  onToggleFavorite={handleToggleFavorite}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </>
    );
  };

  /////* callbacks */////

  useEffect(() => {
    const fetchItems = async () => {
      if (
        !userData?.regions?.length ||
        !userData.regions[0]?.region?.id ||
        !user.id ||
        !selectedCategory.name
      ) {
        return;
      }

      setIsLoading(true);
      try {
        await getItemsByRegionAndDate({
          region: userData.regions[0].region.id,
          date: date.format("YYYY-MM-DD"),
          userId: user.id,
          favoriteOnly: selectedCategory.name === "Favorites",
        }).unwrap();
      } catch (error) {
        console.error("Error fetching items:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchItems();
  }, [date, userData, selectedCategory.name]);

  useEffect(() => {
    if (
      !businessesData &&
      user?.id &&
      userData?.regions?.length > 0 &&
      userData?.regions[0]?.region?.id
    ) {
      getBusinessesByRegion({
        region_id: userData?.regions[0]?.region.id,
        userid: user?.id,
      });
    }
  }, [browseType, userData]);

  useEffect(() => {
    const fetchBusinessListings = async () => {
      if (user?.id) {
        try {
          const response = await authServices.fetchUser(user.id);
          setUserData(response?.data);
        } catch (error: any) {
          console.error("Error fetching user data:", error?.message);
        } finally {
          setIsUserLoading(false);
        }
      }
    };

    fetchBusinessListings();
  }, []);

  useEffect(() => {
    if (businessesData) {
      setBusinessList(businessesData);
      applyCategoryFilter(businessesData);
    }
  }, [businessesData]);

  useEffect(() => {
    if (businessList) {
      applyCategoryFilter(businessList);
    }
  }, [businessList, selectedCategory]);

  return (
    <Box sx={homeScreenStyles.container(theme)}>
      {renderToggleButtons()}

      <Box maxWidth="600px" mx="auto" sx={{ mb: 2 }}>
        {renderSearchBar()}
      </Box>
      <Box marginBottom={homeScreenStyles.categoriesContainer}>
        <AllCategories
          handleActiveCategory={handleActiveCategory}
          activeCategoryIndex={activeCategoryIndex}
          categoryType={[0, 1, 3]}
        />
      </Box>

      <Box>
        <Box width="100%" mb={1} display="flex" flexDirection={"column"}>
          <Box sx={homeScreenStyles.datePickerContainer}>
            {browseType === "bookableExperience" && (
              <DatePickerModal
                date={date}
                handleDateChange={handleDateChange}
                isCalendarVisible={isCalendarVisible}
                setIsCalendarVisible={setIsCalendarVisible}
              />
            )}
            {browseType === "bookableExperience"
              ? !itemsLoading &&
              !isUserLoading &&
              items && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    mt: 1,
                    mb: 2,
                    fontWeight: 400,
                    fontStyle: "normal",
                    fontSize: "15px",
                    lineHeight: "22.5px",
                    letterSpacing: "1.8%",
                  }}
                >
                  {visibleItems?.length || 0} Bookable Items
                </Typography>
              )
              : !businessesLoading &&
              !isUserLoading &&
              filteredBusinesses && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    mt: 1,
                    mb: 2,
                    fontWeight: 400,
                    fontStyle: "normal",
                    fontSize: "15px",
                    lineHeight: "22.5px",
                    letterSpacing: "1.8%",
                  }}
                >
                  {visibleBusinesses?.length || 0} Businesses
                </Typography>
              )}
          </Box>
        </Box>

        {/* <Box sx={homeScreenStyles.cardsContainer}> */}
        {browseType === "business"
          ? renderBusinessCards()
          : renderBookableExperienceCards()}
        {/* </Box> */}
      </Box>
    </Box>
  );
};
